import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { enrollmentService } from '../../api';
import { BookOpen, ArrowRight, LayoutGrid, List } from 'lucide-react';
import toast from 'react-hot-toast';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { ShortCardGrid, ListSection } from '../../components/list/ShortCard';
import { EnrollmentShortCard } from '../../components/list/ShortCardVariants';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';
import { canAccessCourseContent, isEnrollmentCompleted } from '../../utils/enrollmentHelpers';

function EnrollmentList() {
    const [enrollments, setEnrollments] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const [viewMode, setViewMode] = useState('grid');

    const fetchEnrollments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await enrollmentService.getEnrollments({ page, page_size: pageSize });
            const { results, count } = parsePaginated(res);
            setEnrollments(results);
            setTotalCount(count);
        } catch {
            toast.error('Failed to load enrollments');
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchEnrollments();
    }, [fetchEnrollments]);

    const inProgress = enrollments.filter((e) =>
        !isEnrollmentCompleted(e)
        && (canAccessCourseContent(e) || e.status === 'active' || e.status === 'approved')
    );
    const rest = enrollments.filter((e) => !inProgress.includes(e));

    const viewToggle = (
        <div className="ux-list-view-toggle">
            <button type="button" className={viewMode === 'grid' ? 'is-active' : ''} onClick={() => setViewMode('grid')} aria-label="Grid view">
                <LayoutGrid size={18} />
            </button>
            <button type="button" className={viewMode === 'list' ? 'is-active' : ''} onClick={() => setViewMode('list')} aria-label="List view">
                <List size={18} />
            </button>
        </div>
    );

    return (
        <DashboardLayout>
            <main className="page-container content-width ux-list-page">
                <ListPageHeader
                    kicker="My Learning"
                    kickerIcon={BookOpen}
                    title="My Enrollments"
                    description="Track your enrolled courses, progress, and certifications."
                    actions={viewToggle}
                />

                {loading ? (
                    <ListLoadingState label="Loading enrollments…" />
                ) : enrollments.length === 0 ? (
                    <ListEmptyState
                        icon={BookOpen}
                        title="No enrollments yet"
                        description="Browse our courses and enroll to start learning."
                        action={(
                            <Link to="/courses" className="ux-btn-sm ux-btn-primary" style={{ textDecoration: 'none' }}>
                                Browse Courses
                            </Link>
                        )}
                    />
                ) : viewMode === 'grid' ? (
                    <>
                        {inProgress.length > 0 && (
                            <ListSection title="In progress" description="Courses you're actively taking.">
                                <ShortCardGrid>
                                    {inProgress.map((enrollment, index) => (
                                        <EnrollmentShortCard key={enrollment.id} enrollment={enrollment} index={index} />
                                    ))}
                                </ShortCardGrid>
                            </ListSection>
                        )}
                        <ListSection title={inProgress.length ? 'All enrollments' : 'Your courses'}>
                            <ShortCardGrid>
                                {(rest.length ? rest : enrollments).map((enrollment, index) => (
                                    <EnrollmentShortCard key={enrollment.id} enrollment={enrollment} index={index} />
                                ))}
                            </ShortCardGrid>
                        </ListSection>
                    </>
                ) : (
                    <DataRowList>
                        {enrollments.map((enrollment, index) => (
                            <DataRow
                                key={enrollment.id}
                                index={index}
                                leading={<div className="ux-row-thumb"><BookOpen size={20} /></div>}
                                title={enrollment.course_title || 'Course'}
                                subtitle={`Progress: ${enrollment.progress_percentage || 0}%`}
                                badges={<StatusPill status={enrollment.status || 'pending'} label={enrollment.status || 'pending'} />}
                                actions={(
                                    <Link to={`/enrollments/${enrollment.id}`} className="ux-btn-sm ux-btn-primary" style={{ textDecoration: 'none' }}>
                                        Continue <ArrowRight size={14} />
                                    </Link>
                                )}
                            />
                        ))}
                    </DataRowList>
                )}

                {!loading && enrollments.length > 0 && (
                    <ListPagination
                        page={page}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        pageSizes={[8, 12, 24]}
                    />
                )}
            </main>
        </DashboardLayout>
    );
}

export default EnrollmentList;
